import { useEffect, useState } from 'react';
import { HelpButton } from '../../components/HelpButton';
import { Pagination } from '../../components';
import { useNavigate } from 'react-router';
import { partnerActivitiesService, STAFF_PAGE_SIZE } from '../../lib/api-service';
import type { StaffActivity } from '../../lib/types';
import { formatDate, formatTime } from '../../utils/date';
import { Loader2, Users, Eye, EyeOff, Plus, Pencil } from 'lucide-react';

export default function PartnerActivities() {
  const navigate = useNavigate();
  const [activities, setActivities] = useState<StaffActivity[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    partnerActivitiesService
      .list({ page })
      .then((data) => { setActivities(data.results); setTotalCount(data.count); })
      .catch(() => { setActivities([]); setTotalCount(0); })
      .finally(() => setLoading(false));
  }, [page]);

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-primary)' }}>
            Activitățile mele
          </h1>
          <HelpButton to="/staff/documentatie/activitatile-mele" />
        </div>
        <button
          onClick={() => navigate('/staff/activitatile-mele/nou')}
          className="flex items-center gap-2 rounded-lg bg-[var(--vivid-purple)] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[var(--vivid-purple)]/90"
          style={{ fontFamily: 'var(--font-primary)' }}
        >
          <Plus className="h-4 w-4" />
          Activitate Nouă
        </button>
      </div>

      {loading ? (
        <div className="flex min-h-[30vh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-[var(--vivid-purple)]" />
        </div>
      ) : activities.length === 0 ? (
        <div className="py-16 text-center text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
          Nu ai activități asociate.
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-gray-50 text-left" style={{ fontFamily: 'var(--font-primary)' }}>
                <th className="px-4 py-3 font-semibold">Titlu</th>
                <th className="px-4 py-3 font-semibold">Data</th>
                <th className="px-4 py-3 font-semibold">Locație</th>
                <th className="px-4 py-3 font-semibold">Înscriși</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 text-right font-semibold"></th>
              </tr>
            </thead>
            <tbody style={{ fontFamily: 'var(--font-secondary)' }}>
              {activities.map((a) => (
                <tr
                  key={a.id}
                  className="border-b last:border-b-0 hover:bg-gray-50"
                >
                  <td className="px-4 py-3 font-medium">
                    <button onClick={() => navigate(`/staff/activitatile-mele/${a.id}`)} className="hover:text-[var(--vivid-purple)] hover:underline">
                      {a.title}
                    </button>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">
                    {formatDate(a.start_datetime)} {formatTime(a.start_datetime)}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{a.location_text || '—'}</td>
                  <td className="px-4 py-3">
                    {a.requires_registration && a.registration_type !== 'external' ? (
                      <span className="text-xs text-muted-foreground">
                        {a.registration_count}{a.participant_limit ? `/${a.participant_limit}` : ''}
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground/50">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {a.is_published ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-1 text-xs font-semibold text-green-700">
                        <Eye className="h-3 w-3" /> Publicat
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-muted-foreground">
                        <EyeOff className="h-3 w-3" /> Draft
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      {a.requires_registration && a.registration_type !== 'external' && (
                        <button
                          onClick={() => navigate(`/staff/activitatile-mele/${a.slug}/participanti`)}
                          className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-gray-100 hover:text-[var(--vivid-purple)]"
                          title="Participanți"
                        >
                          <Users className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => navigate(`/staff/activitatile-mele/${a.id}`)}
                        className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-gray-100 hover:text-[var(--vivid-purple)]"
                        title="Editează"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Pagination page={page} totalCount={totalCount} pageSize={STAFF_PAGE_SIZE} onPageChange={setPage} />
    </div>
  );
}
